import React, { useEffect, useMemo, useRef, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { BaseSelect } from 'src/components/BaseSelect';
import { ChartEnergyProfile, TChartProfileInfo } from 'src/components/ChartEnergyProfile';
import { ChartEnergyProfileBrush } from 'src/components/ChartEnergyProfile/ChartEnergyProfileBrush';
import { MobileLineChartWrapper } from 'src/components/ChartEnergyProfile/MobileLineChartWrapper';
import { EnergyBadge } from 'src/components/EnergyFlow/components/EnergyFlowContent/components/EnergyBadge';
import { EnergyBageDot } from 'src/components/EnergyFlow/components/EnergyFlowContent/components/EnergyBadgeDot';
import { TEnergyProfileComponentProps } from 'src/components/EnergyProfileComponent/EnergyProfileComponent.types';
import { InfoHelper } from 'src/components/InfoHelper';
import { RegularNoResults } from 'src/components/MapSidebar/components/MapSidebarSingleCommunity/components/RegularNoResults';
import { DataAggregationResolution } from 'src/graphql';
import { useGraphBrush } from 'src/hooks/useGraphBrush';
import {
  selectScreenMode,
  selectSidebarExpanded,
} from 'src/redux/application/application.selectors';
import { selectIsOperationalCommunity } from 'src/redux/scm/scm.selectors';
import { EScreenMode } from 'src/typings/configuration.types';
import { v4 } from 'uuid';

import s from './EnergyProfileComponent.module.scss';

export const EnergyProfileComponent: React.FC<TEnergyProfileComponentProps> = ({
  title,
  info,
  mode,
  initialData,
}) => {
  const { t } = useTranslation();

  const isOperationCommunity = useSelector(selectIsOperationalCommunity);
  const sidebarExpanded = useSelector(selectSidebarExpanded);
  const screenMode = useSelector(selectScreenMode);
  const [localMode, setLocalMode] = useState<DataAggregationResolution | null>(null);
  const localModeRef = useRef<DataAggregationResolution | null>(null);
  const modePropRef = useRef<DataAggregationResolution | null>(null);

  useEffect(() => {
    if (modePropRef.current === mode) return;
    modePropRef.current = mode;
    setLocalMode(mode);
  }, [mode]);

  const { data, graphIndexes, setDebouncedGraphIndexes } = useGraphBrush<TChartProfileInfo>({
    initialData: !!initialData ? initialData : [],
  });
  const [graphKey, setGraphKey] = useState<string>(v4());

  const checkStatement = useMemo(() => !data || data.every((d) => !d || !d.time), [data]);

  useEffect(() => {
    if (localModeRef.current === localMode || isOperationCommunity) return;
    localModeRef.current = localMode;
  }, [localMode, isOperationCommunity]);

  useEffect(() => setGraphKey(v4()), [sidebarExpanded, initialData, localMode, initialData]);

  return (
    <div className={s.wrapper}>
      <div className={s.header}>
        <h3>{title}</h3>
        <InfoHelper info={info} position="bottom" />
      </div>
      {checkStatement && (
        <div className={s.container}>
          <div style={{ height: 300 }}>
            <RegularNoResults communityNotFound={false} />
          </div>
        </div>
      )}
      {!checkStatement && (
        <div className={s.container}>
          {screenMode === EScreenMode.Mobile ? (
            <MobileLineChartWrapper>
              <ChartEnergyProfile data={data} mode={mode} />
            </MobileLineChartWrapper>
          ) : (
            <div className={s.chartArea} style={{ height: 300 }}>
              <ChartEnergyProfile data={data} mode={mode} />
            </div>
          )}
          <div className={s.brushArea}>
            <div className={s.selectWrapper}>
              <BaseSelect
                name="selectedDay"
                value={localMode}
                options={[
                  {
                    label: t('DAILY'),
                    value: DataAggregationResolution.Daily,
                  },
                  {
                    label: t('MONTHLY'),
                    value: DataAggregationResolution.Monthly,
                  },
                ]}
                onChange={({ value }) => {
                  setLocalMode(value as DataAggregationResolution);
                }}
                theme="filled-gray"
                className={s.select}
              />
            </div>
            <div className={s.brushWrapper}>
              {initialData && (
                <ChartEnergyProfileBrush
                  key={`${graphKey}-brush`}
                  data={initialData}
                  setGraphIndexes={(val) => {
                    setDebouncedGraphIndexes(val);
                  }}
                  startIndex={graphIndexes.startIndex}
                  endIndex={graphIndexes.endIndex}
                />
              )}
            </div>
          </div>
          <div className={s.badgeRow}>
            <EnergyBadge
              title={t('GENERATION')}
              size="M"
              icon={<EnergyBageDot backgroundClassname={s.bgGreen} />}
            />
            <EnergyBadge
              title={t('CONSUMPTION')}
              size="M"
              icon={<EnergyBageDot backgroundClassname={s.bgRed} />}
            />
            <EnergyBadge
              title={t('INJECTION')}
              size="M"
              icon={<EnergyBageDot backgroundClassname={s.bgBlue} />}
            />
          </div>
        </div>
      )}
    </div>
  );
};
